import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IGenericNameUrl } from '@shared/interface/generic';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { ProgramMarketingLegacyService } from '@pages/sales-marketing/program-marketing-legacy/program-marketing-legacy.service';
import { FileService } from '@services/file.service';
import { CommonModule } from '@angular/common';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { ImagePreviewComponent } from '@shared/components/image';
import { SectionLoaderComponent } from '@shared/components/loader';

interface IDocumentData extends IGenericNameUrl {
  size?: number | null;
}

@Component({
  selector: 'app-document-preview',
  standalone: true,
  imports: [CommonModule, InlineSVGModule, ImagePreviewComponent, SectionLoaderComponent],
  templateUrl: './document-preview.component.html',
  styleUrls: ['./document-preview.component.scss'],
})
export class DocumentPreviewComponent implements OnInit, OnChanges {
  @Input() data: IDocumentData[];
  @Input() customClass?: any;
  // Thumbnail 60x60 + border-radius 4px (LPJ Klaim, konfirmasi user berulang) — opsional, default
  // tetap 200px/tanpa radius biar konsumen app-document-preview lain gak kena dampak.
  @Input() imageHeight?: number;
  @Input() imageWidth?: number;
  @Input() imageBorderRadius?: number;
  @Input() imageClass?: string;
  isLoadingSubject = new BehaviorSubject(false);

  // Banyak consumer manggil fungsi (mis. toDocumentPreview(urls)) langsung di [data] — bikin
  // array baru tiap CD cycle, ngOnChanges salah deteksi "berubah" terus & getFileSize() nembak
  // berkali-kali tanpa henti. Bandingkan by URL content, bukan reference identity.
  private lastDataKey: string | null = null;

  constructor(
    private fileService: FileService,
    private promagService: ProgramMarketingLegacyService
  ) {}

  ngOnInit() {
    this.initMapData();
  }

  initMapData() {
    const key = (this.data ?? []).map((d) => d.url).join('|');
    if (key === this.lastDataKey) return;
    this.lastDataKey = key;
    this.getFileSize();
  }

  getFileSize() {
    if (!Array.isArray(this.data) || this.data.length === 0) return;
    // size cuma dipakai previewFile (PDF), skip fetch buat IMAGE. catchError wajib: 1 request
    // gagal (mis. CORS) bikin forkJoin macet loading selamanya kalau gak ditangkap.
    const hasFileDocs = this.data.some((item) => this.getType(item) === 'FILE');
    if (!hasFileDocs) return;

    this.isLoadingSubject.next(true);
    const _requests = this.data.map((item) => (this.getType(item) === 'FILE' ? this.fileService.getFileSize(item.url).pipe(catchError(() => of(null))) : of(null)));
    forkJoin(_requests).subscribe((res) => {
      this.data = this.data.map((item, index) => ({
        ...item,
        size: res[index] ? this.fileService.convertBytesToMb(res[index] as number) : null,
      }));
      this.isLoadingSubject.next(false);
    });
  }

  handleDownload(doc: IDocumentData) {
    this.isLoadingSubject.next(true);
    this.fileService.downloadPdfFile({ name: doc.name, url: doc.url }).subscribe((resp) => {
      if (!resp) return;
      this.isLoadingSubject.next(false);
    });
  }

  getType = (doc: IGenericNameUrl) => this.promagService.getTypeDoc(doc.url);

  // Root cause (2026-08-24): consumer manggil fungsi (mis. `toDocumentPreview(urls)`) langsung di
  // [data] (lihat komentar lastDataKey di atas) — tiap CD cycle balikin array BARU isi objek
  // literal BARU juga, walau `url`-nya sama. *ngFor default (tanpa trackBy) nge-diff by OBJECT
  // REFERENCE per item, jadi nganggep SEMUA item ganti identitas tiap cycle → destroy+recreate
  // SEMUA `<app-image-preview>` (termasuk modal yang lagi kebuka) literally tiap CD cycle
  // (terbukti via MutationObserver: churn kejadian <1ms setelah klik thumbnail). NgbModal window
  // yang udah kebuka gak ikut ke-dismiss otomatis (ModalComponent gak ada ngOnDestroy cleanup),
  // jadi modal jadi "orphan" — kelihatan kebuka tapi komponennya udah mati, semua tombol dead.
  // trackBy by url (stable business key) nyetop churn ini di sumbernya.
  trackByUrl = (_: number, doc: IGenericNameUrl) => doc.url;

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.data && !changes.data.firstChange) this.initMapData();
  }

  protected readonly STRING_CONSTANTS = STRING_CONSTANTS;
}
